import { router } from '@inertiajs/react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import type { JudgeBallotProject, JudgingCriterion } from '@/types';
import {
  clearPending,
  registerPending,
  saveScores,
  trackSave,
} from './autosave';
import { formatScore, parseScore, scoreError } from './scoring';

// How long after the judge stops typing a change is saved.
const SAVE_DEBOUNCE_MS = 800;

export type SaveStatus = 'idle' | 'saving' | 'saved' | 'error';

// A judge's grades and internal note for one project, as they are being typed: the
// drafts, their validation, and the background autosave. Shared by the stage page's
// rows and the final check's compact rows.
export function useProjectGrades({
  stageSlug,
  project,
  criteria,
}: {
  stageSlug: string;
  project: JudgeBallotProject;
  criteria: JudgingCriterion[];
}) {
  // null while the judge is not editing: the inputs then show the saved grades, so a
  // poll that brings new ones in never fights with what is being typed.
  const [draft, setDraft] = useState<Record<string, string> | null>(null);
  const [noteDraft, setNoteDraft] = useState<string | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [saveStatus, setSaveStatus] = useState<SaveStatus>('idle');
  // The latest drafts, for the debounced save and for telling whether the judge kept
  // typing while a save was in flight.
  const draftRef = useRef(draft);
  const noteDraftRef = useRef(noteDraft);
  draftRef.current = draft;
  noteDraftRef.current = noteDraft;
  const timer = useRef<number | undefined>(undefined);
  const mounted = useRef(true);

  const valueOf = (criterionId: string) =>
    draft?.[criterionId] ?? formatScore(project.scores[criterionId]);

  const errorOf = (criterionId: string) =>
    scoreError(valueOf(criterionId)) ?? errors[`scores.${criterionId}`];
  const invalid = (criterionId: string) => scoreError(valueOf(criterionId)) !== null;

  const parsed = criteria.map((criterion) => ({
    criterion,
    score: parseScore(valueOf(criterion.id)),
  }));
  const complete = criteria.length > 0 && parsed.every((p) => p.score !== null);
  // The weighted grade of what is typed right now, or null until every criterion is.
  const weighted = complete
    ? parsed.reduce(
        (sum, { criterion, score }) => sum + ((score ?? 0) * criterion.weight) / 100,
        0,
      )
    : null;
  const hasInvalid = criteria.some((criterion) => invalid(criterion.id));
  const dirty = draft !== null || noteDraft !== null;
  const note = noteDraft ?? project.note ?? '';

  const setValue = (criterionId: string, value: string) => {
    setDraft({
      ...Object.fromEntries(
        criteria.map((c) => [c.id, formatScore(project.scores[c.id])]),
      ),
      ...draft,
      [criterionId]: value,
    });
  };

  // Saves whatever is pending right now, in the background. A grade that is not valid
  // yet holds the row back: it is flagged on the field and saved once it is fixed.
  const save = useCallback((): Promise<void> => {
    window.clearTimeout(timer.current);
    clearPending(project.id);

    const scores = draftRef.current;
    const note = noteDraftRef.current;
    if (scores === null && note === null) return Promise.resolve();
    if (scores && Object.values(scores).some((value) => scoreError(value))) {
      return Promise.resolve();
    }

    if (mounted.current) setSaveStatus('saving');
    const url = `/judges/stages/${stageSlug}/projects/${project.id}/scores`;
    const payload = { scores: scores ?? {}, ...(note !== null && { note }) };

    return trackSave(
      saveScores(url, payload).then(
        (result) =>
          new Promise<void>((resolve) => {
            // The row is gone (the judge left the page): the save still landed.
            if (!mounted.current) return resolve();

            if (!result.ok) {
              setErrors(result.errors);
              setSaveStatus('error');
              toast.error(`No se pudieron guardar las notas de ${project.name}`);
              return resolve();
            }

            // Re-read the ballot so the saved grades (and the ranking) come from the
            // server, then drop the drafts — unless the judge kept typing meanwhile,
            // in which case that newer draft saves next.
            router.reload({
              only: ['ballot'],
              onSuccess: () => {
                if (draftRef.current === scores) setDraft(null);
                if (noteDraftRef.current === note) setNoteDraft(null);
                setErrors({});
                setSaveStatus('saved');
              },
              onFinish: () => resolve(),
            });
          }),
      ),
    );
  }, [stageSlug, project.id, project.name]);

  // Every change saves itself SAVE_DEBOUNCE_MS after the judge stops typing; until
  // then the page knows it is pending, so leaving for the final check flushes it.
  useEffect(() => {
    if (draft === null && noteDraft === null) return;
    if (draft && Object.values(draft).some((value) => scoreError(value))) {
      clearPending(project.id);
      return;
    }

    registerPending(project.id, save);
    timer.current = window.setTimeout(save, SAVE_DEBOUNCE_MS);
    return () => window.clearTimeout(timer.current);
  }, [draft, noteDraft, project.id, save]);

  // Leaving the page (any link) saves what is still waiting for its debounce.
  const saveRef = useRef(save);
  saveRef.current = save;
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      void saveRef.current();
    };
  }, []);

  // Leaving a field saves at once rather than waiting for the debounce.
  const saveNow = () => {
    if (dirty && !hasInvalid) save();
  };

  return {
    valueOf,
    errorOf,
    invalid,
    setValue,
    note,
    setNote: setNoteDraft,
    errors,
    weighted,
    hasInvalid,
    saveNow,
    // What the save indicator shows.
    status: (hasInvalid ? 'invalid' : dirty ? 'saving' : saveStatus) as
      | SaveStatus
      | 'invalid',
  };
}
